import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import Flex from 'components/common/Flex';
import { useAppContext } from 'providers/AppProvider';
import { Card, Spinner, Row, Col } from 'react-bootstrap';
import { allReqSummary } from 'hooks/CallApi';

const SummaryChart = () => {
  const chartRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 1536);
  const [isLoading, setIsLoading] = useState(true); // 로딩 상태 추가

  // const parseDuration = (duration) => {
  //   if (typeof duration !== 'string') return 0; // 문자열이 아니면 0 반환
  //   const numericPart = duration.replace("PT", "").replace("S", "");
  //   return Math.round(parseFloat(numericPart) * 10) / 10; // 소수 첫째 자리로 반올림
  // };

  const [chartData, setChartData] = useState({
    totalRequests: 0,
    apiRequests: 0,
    cloudFunctionsRequests: 0,
    sdkRequests: 0,

    apirequestPer: 0,
    cfrequestPer: 0,
    sdkrequestPer: 0,

    avgAPICalDuration: 0,
    avgCFCalDuration: 0,
    avgSDKCalDuration: 0,

    failAPIRequestPer: 0,
    failCFRequestPer: 0,
    failSDKRequestPer: 0,
    totalCompanyCnt: 0
  });
  const {
    config: {
      dashboardDate,
      dashboardDateValue,
      Chart_startdate,
      Chart_enddate,
      isDemo
    },
    setConfig
  } = useAppContext();
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 1536);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [dateRange, setDateRange] = useState({
    start: Chart_startdate,
    end: Chart_enddate
  });
  useEffect(() => {
    // Chart_startdate 또는 Chart_enddate가 변경될 때 상태 업데이트
    setDateRange({
      start: Chart_startdate,
      end: Chart_enddate
    });
  }, [Chart_startdate, Chart_enddate]);
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true); // 데이터 로드 시작 시 로딩 상태 활성화

      try {
        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // API 호출
        const response = await allReqSummary({
          period: dashboardDateValue,
          start: dateRange.start,
          end: dateRange.end,
          timeZone: clientTimeZone,
          demo: isDemo
        });
        if (cancelled) return;
        const data = response.data; // API 응답에서 첫 번째 데이터 사용

        // API 응답 데이터를 상태에 저장
        setChartData({
          totalRequests: data.totalRequestCnt || 0,
          apiRequests: data.totalAPIRequestCnt || 0,
          cloudFunctionsRequests: data.totalCFRequestCnt || 0,
          sdkRequests: data.totalSDKRequestCnt || 0,

          apirequestPer: data.apirequestPer || 0,
          cfrequestPer: data.cfrequestPer || 0,
          sdkrequestPer: data.sdkrequestPer || 0,

          avgAPICalDuration: data.avgAPICalDuration || 0,
          avgCFCalDuration: data.avgCFCalDuration || 0,
          avgSDKCalDuration: data.avgSDKCalDuration || 0,

          failAPIRequestPer: data.failAPIRequestPer || 0,
          failCFRequestPer: data.failCFRequestPer || 0,
          failSDKRequestPer: data.failSDKRequestPer || 0,
          totalCompanyCnt: data.totalCompanyCnt || 0
        });
      } catch (error) {
        if (cancelled) return;
        console.error('API 데이터 조회 실패:', error);
      } finally {
        if (!cancelled) setIsLoading(false); // 데이터 로드 완료 시 로딩 상태 비활성화
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [dashboardDate, dateRange, isDemo]);

  useEffect(() => {
    if (isLoading) return; // 로딩 중일 때는 차트를 렌더링하지 않음

    const chartInstance = echarts.init(chartRef.current);

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: params => {
          const { name, value } = params;
          return `${name}: ${value.toLocaleString()}건 (${params.percent}%)`;
        }
      },
      legend: {
        orient: 'horizontal',
        bottom: 2,
        textStyle: { fontSize: 10 },
        data: ['API', 'CloudFunctions', 'SDK']
      },
      series: [
        {
          type: 'pie',
          radius: ['65%', '85%'],
          center: ['50%', '70%'],
          startAngle: 180,
          endAngle: 0,
          avoidLabelOverlap: false,

          itemStyle: {
            borderRadius: 10,
            borderColor: '#FFFFFF',
            borderWidth: 5,
          },
          label: {
            show: true,
            position: 'center',
            offset: [0, -15],
            formatter: [
              `{count|${chartData.totalRequests.toLocaleString()}}`,
              '{total|총 요청량}'
            ].join('\n'),
            rich: {
              count: {
                fontSize: 36,
                color: '#2C3E50',
                fontWeight: '800',
                fontFamily: 'Arial, sans-serif',
                padding: [0, 0, 8, 0]
              },
              total: {
                fontSize: 14,
                color: '#8A94A6',
                fontWeight: '500'
              }
            }
          },
          labelLine: { show: false },
          data: [
            { value: chartData.apiRequests, name: 'API' },
            { value: chartData.cloudFunctionsRequests, name: 'CloudFunctions' },
            { value: chartData.sdkRequests, name: 'SDK' }
          ],
          color: ['#FFB74D', '#81C784', '#64B5F6'] // 파스텔 오렌지, 민트, 블루
        }
      ]
    };

    chartInstance.setOption(option);
    chartInstance.resize();

    const handleResizeChart = () => {
      if (chartInstance) {
        chartInstance.resize();
      }
    };
    window.addEventListener('resize', handleResizeChart);

    return () => {
      window.removeEventListener('resize', handleResizeChart);
      chartInstance.dispose();
    };
  }, [chartData]);
  // if (isLoading) {
  //   // 로딩 상태일 때 스피너를 표시
  //   return (
  //     <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
  //       <Spinner animation="border" role="status">
  //         <span className="visually-hidden">Loading...</span>
  //       </Spinner>
  //     </div>
  //   );
  // }
  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: 'none',
          backgroundColor: 'transparent'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>전체 API 호출량</h5>
        </div>
        <span
          style={{
            marginLeft: '8px',
            marginTop: 0,
            marginBottom: 0,
            padding: '2px 8px',
            // backgroundColor: '#A1C4E3',
            backgroundColor: '#2C7BE5',
            color: 'white',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}
        >
          {dashboardDate}
        </span>
      </Card.Header>

      <Card.Body as={Flex} alignItems="center" className="px-0">
        {isLoading ? (
          // 로딩 중일 때 카드 내부에 스피너 표시
          <div
            style={{
              width: '100%',
              height: '15rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <Row className="w-100 align-items-center m-0">
            <Col xs={12} md={3} className="text-center mb-3 mb-md-0">
              <div style={{ fontSize: '0.9rem' }}>
                <strong style={{ wordBreak: 'keep-all' }}>전체 고객사 수</strong>
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#111827' }}>
                {chartData.totalCompanyCnt}
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div
                ref={chartRef}
                style={{ width: '100%', minHeight: '200px' }}
              ></div>
            </Col>
            <Col xs={12} md={3}>
              <div style={{ fontSize: '0.75rem', marginBottom: '10px' }}>
                <strong>
                  인증키 타입별 평균 처리 시간, <br />
                  요청 중 실패 비율
                </strong>
              </div>
              <div>
                <div style={{ fontSize: '1em', fontWeight: 'bold', marginBottom: '2%', color: '#FFB74D' }}>
                  API
                </div>
                <div style={{ color: '#4B5563', fontWeight: '500' }}>
                  {chartData.avgAPICalDuration}초{' '}
                  <span style={{ color: '#9CA3AF', fontSize: '0.8em', marginLeft: '5px' }}>
                    {Number(chartData.failAPIRequestPer).toFixed(1)}% fails
                  </span>
                </div>
                <div style={{ fontSize: '1em', fontWeight: 'bold', marginBottom: '2%', color: '#81C784', marginTop: '5%' }}>
                  CloudFunctions
                </div>
                <div style={{ color: '#4B5563', fontWeight: '500' }}>
                  {chartData.avgCFCalDuration}초{' '}
                  <span style={{ color: '#9CA3AF', fontSize: '0.8em', marginLeft: '5px' }}>
                    {Number(chartData.failCFRequestPer).toFixed(1)}% fails
                  </span>
                </div>
                <div style={{ fontSize: '1em', fontWeight: 'bold', marginBottom: '2%', color: '#64B5F6', marginTop: '5%' }}>
                  SDK
                </div>
                <div style={{ color: '#4B5563', fontWeight: '500' }}>
                  {chartData.avgSDKCalDuration}초{' '}
                  <span style={{ color: '#9CA3AF', fontSize: '0.8em', marginLeft: '5px' }}>
                    {Number(chartData.failSDKRequestPer).toFixed(1)}% fails
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

const metricAPIStyle = {
  fontSize: '1em',
  fontWeight: 'bold',
  marginBottom: '2%',
  color: '#F26C4F'
};

const metricCloudFunctionsStyle = {
  fontSize: '1em',
  fontWeight: 'bold',
  marginBottom: '2%',
  color: '#1E3A8A',
  marginTop: '5%'
};
const metricSDKStyle = {
  fontSize: '1em',
  fontWeight: 'bold',
  marginBottom: '2%',
  color: '#00B4D8',
  marginTop: '5%'
};

const labelStyle = {
  color: '#666',
  fontSize: '0.7em'
};

export default SummaryChart;
